import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const Settings = () => {
  const { user, updateProfile } = useAuth();
  const [settings, setSettings] = useState(null);
  const [apiKeys, setApiKeys] = useState({});
  const [models, setModels] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [settingsRes, modelsRes] = await Promise.all([
        axios.get('/api/users/settings'),
        axios.get('/api/users/models')
      ]);
      
      setSettings(settingsRes.data.settings);
      setApiKeys({
        openai: '',
        anthropic: '',
        cohere: ''
      });
      setModels(modelsRes.data.models);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveApiKey = async (provider) => {
    const key = apiKeys[provider];
    if (!key) {
      toast.error('Please enter an API key');
      return;
    }

    try {
      await axios.post('/api/users/apikeys', { provider, key });
      toast.success(`${provider} API key saved`);
      setApiKeys({ ...apiKeys, [provider]: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save API key');
    }
  };

  const updateSettings = async (updates) => {
    try {
      await axios.put('/api/users/settings', updates);
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Settings</h1>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#2d2d3d]">
        {['profile', 'api-keys', 'preferences', 'inference', 'connections'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'text-indigo-400 border-b-2 border-indigo-400'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {tab.replace('-', ' ')}
          </button>
        ))}
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6 max-w-2xl">
          <h3 className="text-lg font-semibold mb-4">Profile Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Name</label>
              <input
                type="text"
                defaultValue={user?.name}
                onBlur={(e) => updateProfile({ name: e.target.value })}
                className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Email</label>
              <input
                type="email"
                value={user?.email}
                disabled
                className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-gray-500 cursor-not-allowed"
              />
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4">Change Password</h3>
            <ChangePasswordForm />
          </div>
        </div>
      )}

      {/* API Keys Tab */}
      {activeTab === 'api-keys' && (
        <div className="space-y-6 max-w-2xl">
          <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-2xl p-6">
            <h3 className="text-lg font-semibold mb-2">AI Provider API Keys</h3>
            <p className="text-gray-400 text-sm mb-4">
              Add your own API keys to use with your agents. Your keys are encrypted and never shared.
            </p>
          </div>

          {/* OpenAI */}
          <ApiKeyCard
            provider="openai"
            name="OpenAI"
            description="For GPT-4, GPT-3.5, and other OpenAI models"
            icon="🤖"
            value={apiKeys.openai}
            onChange={(val) => setApiKeys({ ...apiKeys, openai: val })}
            onSave={() => saveApiKey('openai')}
            models={models.openai}
          />

          {/* Anthropic */}
          <ApiKeyCard
            provider="anthropic"
            name="Anthropic"
            description="For Claude 3 Opus, Sonnet, and Haiku models"
            icon="🧠"
            value={apiKeys.anthropic}
            onChange={(val) => setApiKeys({ ...apiKeys, anthropic: val })}
            onSave={() => saveApiKey('anthropic')}
            models={models.anthropic}
          />

          {/* Cohere */}
          <ApiKeyCard
            provider="cohere"
            name="Cohere"
            description="For Command and other Cohere models"
            icon="⚡"
            value={apiKeys.cohere}
            onChange={(val) => setApiKeys({ ...apiKeys, cohere: val })}
            onSave={() => saveApiKey('cohere')}
            models={models.cohere}
          />
        </div>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6 max-w-2xl">
          <h3 className="text-lg font-semibold mb-4">Preferences</h3>
          <div className="space-y-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Default AI Model</label>
              <select
                value={settings?.defaultModel}
                onChange={(e) => updateSettings({ defaultModel: e.target.value })}
                className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
              >
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="cohere">Cohere</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Theme</label>
              <select
                value={settings?.theme}
                onChange={(e) => updateSettings({ theme: e.target.value })}
                className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
              >
                <option value="dark">Dark</option>
                <option value="light">Light</option>
                <option value="system">System</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Language</label>
              <select
                value={settings?.language}
                onChange={(e) => updateSettings({ language: e.target.value })}
                className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="zh">Chinese</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Max Agents</label>
              <input
                type="number"
                value={settings?.maxAgents}
                onChange={(e) => updateSettings({ maxAgents: parseInt(e.target.value) })}
                min="1"
                max="50"
                className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Max Tokens Per Request</label>
              <input
                type="number"
                value={settings?.maxTokensPerRequest}
                onChange={(e) => updateSettings({ maxTokensPerRequest: parseInt(e.target.value) })}
                min="100"
                max="10000"
                step="100"
                className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-gray-500">Receive email updates about your tasks</p>
              </div>
              <button
                onClick={() => updateSettings({
                  notifications: { ...settings?.notifications, email: !settings?.notifications?.email }
                })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings?.notifications?.email ? 'bg-indigo-500' : 'bg-gray-600'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  settings?.notifications?.email ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inference Tab */}
      {activeTab === 'inference' && (
        <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6 max-w-2xl">
          <h3 className="text-lg font-semibold mb-4">Inference Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Ollama URL</label>
              <input
                type="text"
                defaultValue={process.env.REACT_APP_OLLAMA_URL || 'http://localhost:11434'}
                disabled
                className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Configured via server environment variable</p>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Default Routing</label>
              <select
                value={settings?.inferenceRouting?.autoRouting ? 'auto' : 'manual'}
                onChange={(e) => updateSettings({
                  inferenceRouting: { ...settings?.inferenceRouting, autoRouting: e.target.value === 'auto' }
                })}
                className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
              >
                <option value="auto">Auto (Budget-Optimized)</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Prefer Local Inference</p>
                <p className="text-sm text-gray-500">Route simple tasks to free local Ollama</p>
              </div>
              <button
                onClick={() => updateSettings({
                  inferenceRouting: { ...settings?.inferenceRouting, preferLocal: !settings?.inferenceRouting?.preferLocal }
                })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings?.inferenceRouting?.preferLocal !== false ? 'bg-indigo-500' : 'bg-gray-600'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  settings?.inferenceRouting?.preferLocal !== false ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Budget Alerts</p>
                <p className="text-sm text-gray-500">Get notified when approaching spending limits</p>
              </div>
              <button
                onClick={() => updateSettings({
                  inferenceRouting: { ...settings?.inferenceRouting, budgetAlerts: !settings?.inferenceRouting?.budgetAlerts }
                })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings?.inferenceRouting?.budgetAlerts !== false ? 'bg-indigo-500' : 'bg-gray-600'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  settings?.inferenceRouting?.budgetAlerts !== false ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connections Tab */}
      {activeTab === 'connections' && (
        <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6 max-w-2xl">
          <h3 className="text-lg font-semibold mb-4">Connections Summary</h3>
          <p className="text-gray-400 mb-4">
            Manage your external connections (Claude Code CLI, Windsurf, OpenClaw bots) from the dedicated Connections page.
          </p>
          <a
            href="/connections"
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
          >
            🔌 Manage Connections
          </a>
        </div>
      )}
    </div>
  );
};

const ApiKeyCard = ({ provider, name, description, icon, value, onChange, onSave, models }) => {
  const [showKey, setShowKey] = useState(false);

  return (
    <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-700 to-gray-600 flex items-center justify-center text-2xl">
            {icon}
          </div>
          <div>
            <h4 className="font-semibold">{name}</h4>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
        {models && models.length > 0 && (
          <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-lg text-xs">
            Connected
          </span>
        )}
      </div>

      <div className="flex gap-2">
        <div className="flex-1 relative">
          <input
            type={showKey ? 'text' : 'password'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${name} API key`}
            className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white pr-10"
          />
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
          >
            {showKey ? '🙈' : '👁️'}
          </button>
        </div>
        <button
          onClick={onSave}
          disabled={!value}
          className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          Save
        </button>
      </div>

      {models && models.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#2d2d3d]">
          <p className="text-sm text-gray-500 mb-2">Available Models:</p>
          <div className="flex flex-wrap gap-2">
            {models.slice(0, 5).map((model) => (
              <span key={model.id} className="px-2 py-1 bg-[#0a0a0f] rounded text-xs text-gray-400">
                {model.name}
              </span>
            ))}
            {models.length > 5 && (
              <span className="px-2 py-1 bg-[#0a0a0f] rounded text-xs text-gray-400">
                +{models.length - 5} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ChangePasswordForm = () => {
  const [form, setForm] = useState({ current: '', new: '', confirm: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new !== form.confirm) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.put('/api/auth/password', {
        currentPassword: form.current,
        newPassword: form.new
      });
      toast.success('Password changed');
      setForm({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm text-gray-400 mb-1">Current Password</label>
        <input
          type="password"
          value={form.current}
          onChange={(e) => setForm({ ...form, current: e.target.value })}
          className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
          required
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">New Password</label>
        <input
          type="password"
          value={form.new}
          onChange={(e) => setForm({ ...form, new: e.target.value })}
          className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
          required
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Confirm New Password</label>
        <input
          type="password"
          value={form.confirm}
          onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
          required
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg transition-colors"
      >
        {loading ? 'Changing...' : 'Change Password'}
      </button>
    </form>
  );
};

export default Settings;
